import React, { useContext, useEffect, useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import OptionSwitch from "./OptionSwitch"
import { DOWNLOAD_TYPES, INPUTS } from "hooks/budgeting/useDownloadBudgetConfig"
import BudgetExportSelect from "./BudgetExportSelect"
import SubscriptionContext from "context/subscription/subscriptionContext"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import {
  EXPORT_PDF_INFO,
  EXPORT_PDF_GENERALS,
  EXPORT_PDF_DECOMPOSITION,
  EXPORT_PDF_MEASUREMENTS,
  EXPORT_PDF_COVER,
  EXPORT_PDF_SIGNATURE,
  EXPORT_PDF_DATE,
  GRANTED,
  EXPORT_PDF_SUMMARY,
  EXPORT_LISTO_PDF,
} from "constants/entitlements"
import DownloadBudgetEnum from "common/DownloadBudgetEnum"

const BRIEFING_SELECT_NONE = "__none__"

const blurActiveElement = () => {
  document.activeElement?.blur?.()
}

const PdfOptions = ({
  setDownloadBudgetConfig,
  clientPriceRef,
  clientPrice,
  setClientPrice,
  companyAndCustomerInfo,
  setCompanyAndCustomerInfo,
  generalConditions,
  setGeneralConditions,
  decomposition,
  setDecomposition,
  pdfMeasurements,
  setPdfMeasurements,
  briefingPosition,
  setBriefingPosition,
  briefingPositionRef,
  addCover,
  setAddCover,
  addSignature,
  setAddSignature,
  addDate,
  setAddDate,
  bordes,
  setBordes,
  fromTab,
  certificationAtOrigin: certificationAtOriginProp,
  setCertificationAtOrigin: setCertificationAtOriginProp,
  includeBudget: includeBudgetProp,
  setIncludeBudget: setIncludeBudgetProp,
  certificationPdfFormat: certificationPdfFormatProp,
  setCertificationPdfFormat: setCertificationPdfFormatProp,
  includeUncertifiedLineItems: includeUncertifiedLineItemsProp,
  setIncludeUncertifiedLineItems: setIncludeUncertifiedLineItemsProp,
}) => {
  const tipo = DOWNLOAD_TYPES.PDF
  const [internalCertAtOrigin, setInternalCertAtOrigin] = useState(false)
  const useLifted =
    typeof setCertificationAtOriginProp === "function" &&
    typeof certificationAtOriginProp === "boolean"
  const certificationAtOrigin = useLifted
    ? certificationAtOriginProp
    : internalCertAtOrigin
  const setCertificationAtOrigin = useLifted
    ? setCertificationAtOriginProp
    : setInternalCertAtOrigin
  const [internalIncludeBudget, setInternalIncludeBudget] = useState(false)
  const useLiftedInclude =
    typeof setIncludeBudgetProp === "function" &&
    typeof includeBudgetProp === "boolean"
  const includeBudget = useLiftedInclude
    ? includeBudgetProp
    : internalIncludeBudget
  const setIncludeBudget = useLiftedInclude
    ? setIncludeBudgetProp
    : setInternalIncludeBudget
  const [
    internalIncludeUncertifiedLineItems,
    setInternalIncludeUncertifiedLineItems,
  ] = useState(false)
  const useLiftedUncertified =
    typeof setIncludeUncertifiedLineItemsProp === "function" &&
    typeof includeUncertifiedLineItemsProp === "boolean"
  const includeUncertifiedLineItems = useLiftedUncertified
    ? includeUncertifiedLineItemsProp
    : internalIncludeUncertifiedLineItems
  const setIncludeUncertifiedLineItems = useLiftedUncertified
    ? setIncludeUncertifiedLineItemsProp
    : setInternalIncludeUncertifiedLineItems
  const [internalCertificationPdfFormat, setInternalCertificationPdfFormat] =
    useState("detailed")
  const useLiftedCertFormat =
    typeof setCertificationPdfFormatProp === "function" &&
    typeof certificationPdfFormatProp === "string"
  const certificationPdfFormat = useLiftedCertFormat
    ? certificationPdfFormatProp
    : internalCertificationPdfFormat
  const setCertificationPdfFormat = useLiftedCertFormat
    ? setCertificationPdfFormatProp
    : setInternalCertificationPdfFormat
  const isCertTab =
    fromTab === DownloadBudgetEnum.FROM_CERTIFICATION ||
    fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION
  const { subscriptionState } = useContext(SubscriptionContext)
  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  useEffect(() => {
    if (
      fromTab === DownloadBudgetEnum.FROM_CERTIFICATION ||
      fromTab === DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION
    ) {
      if (briefingPosition !== false) {
        setBriefingPosition(false)
        setDownloadBudgetConfig(tipo, INPUTS.BRIEFING_POSITION, false)
      }
    }
  }, [
    fromTab,
    briefingPosition,
    setBriefingPosition,
    setDownloadBudgetConfig,
    tipo,
  ])

  const entitlementDialogTitle = (configKey, titleOverride) => {
    if (titleOverride) return titleOverride
    const i18nKeyByConfigKey = {
      [INPUTS.PDF_MEASUREMENTS]: "measurements",
    }
    const i18nKey = i18nKeyByConfigKey[configKey] ?? configKey
    return i18n.t(`downloadBudget.${i18nKey}`)
  }

  const handleEntitlementCheck = (
    permission,
    state,
    setState,
    configKey,
    titleOverride,
  ) => {
    const hasPermission =
      permission === GRANTED ||
      subscriptionState?.subscription_entitlements?.some(
        ent => ent.feature_name === permission && ent.is_enabled === true,
      )

    const handleBlocked = () => {
      checkEntitlement(
        permission,
        entitlementDialogTitle(configKey, titleOverride),
      )
    }

    useEffect(() => {
      if (!hasPermission && state) {
        setState(false)
        setDownloadBudgetConfig(tipo, configKey, false)
      }
    }, [
      hasPermission,
      state,
      setState,
      setDownloadBudgetConfig,
      tipo,
      configKey,
    ])

    return hasPermission ? null : handleBlocked
  }

  const handleExportPdfInfoBlocked = handleEntitlementCheck(
    EXPORT_PDF_INFO,
    companyAndCustomerInfo,
    setCompanyAndCustomerInfo,
    INPUTS.COMPANY_AND_CLIENT_INFO,
  )

  const handleExportPdfGeneralsBlocked = handleEntitlementCheck(
    EXPORT_PDF_GENERALS,
    generalConditions,
    setGeneralConditions,
    INPUTS.GENERAL_CONDITIONS,
  )

  const handleExportPdfDecompositionBlocked = handleEntitlementCheck(
    EXPORT_PDF_DECOMPOSITION,
    decomposition,
    setDecomposition,
    INPUTS.PART_DECOMPOSITION,
  )

  const handleExportPdfMeasurementsBlocked = handleEntitlementCheck(
    EXPORT_PDF_MEASUREMENTS,
    pdfMeasurements,
    setPdfMeasurements,
    INPUTS.PDF_MEASUREMENTS,
  )

  const handleExportPdfCoverBlocked = handleEntitlementCheck(
    EXPORT_PDF_COVER,
    addCover,
    setAddCover,
    INPUTS.ADD_COVER,
  )

  const handleExportPdfSignatureBlocked = handleEntitlementCheck(
    EXPORT_PDF_SIGNATURE,
    addSignature,
    setAddSignature,
    INPUTS.ADD_SIGNATURE,
  )

  const handleExportPdfDateBlocked = handleEntitlementCheck(
    EXPORT_PDF_DATE,
    addDate,
    setAddDate,
    INPUTS.ADD_DATE,
  )

  const certificationAtOriginSwitch = {
    i18nKey: "downloadBudget.certificationAtOrigin",
    testId: "download-budget-pdf-certification-at-origin-switch",
    state: certificationAtOrigin,
    setState: setCertificationAtOrigin,
    key: `pdf_${INPUTS.CERTIFICATION_AT_ORIGIN}`,
    inputKey: INPUTS.CERTIFICATION_AT_ORIGIN,
    tipo: tipo,
    setDownloadBudgetConfig: setDownloadBudgetConfig,
    onToggleBlocked: handleEntitlementCheck(
      EXPORT_LISTO_PDF,
      certificationAtOrigin,
      setCertificationAtOrigin,
      INPUTS.CERTIFICATION_AT_ORIGIN,
      i18n.t("certHistory.downloadCertificationOriginTooltip"),
    ),
  }

  const selectPriceOptions = [
    { value: "client", label: i18n.t("downloadBudget.clientPrice") },
    { value: "blind", label: i18n.t("downloadBudget.noPrice") },
    { value: "cost", label: i18n.t("downloadBudget.targetCost") },
    { value: "costReal", label: i18n.t("downloadBudget.actualCost") },
  ]

  const briefingSelectOptions = [
    {
      value: BRIEFING_SELECT_NONE,
      label: i18n.t("downloadBudget.noSummary"),
    },
    { value: "top", label: i18n.t("downloadBudget.summaryAtStart") },
    { value: "bottom", label: i18n.t("downloadBudget.summaryAtEnd") },
  ]

  const certificationPdfFormatOptions = [
    {
      value: "detailed",
      label: i18n.t("downloadBudget.certificationPdfFormatDetailed"),
    },
    {
      value: "compacted",
      label: i18n.t("downloadBudget.certificationPdfFormatCompacted"),
    },
  ]

  const handleCertificationPdfFormatChange = event => {
    const v = event.target.value
    setCertificationPdfFormat(v)
    setDownloadBudgetConfig(tipo, INPUTS.CERTIFICATION_PDF_FORMAT, v)
  }

  const handleBriefingPositionChange = event => {
    const raw = event.target.value
    const next = raw === BRIEFING_SELECT_NONE ? false : raw
    const hasPermission =
      EXPORT_PDF_SUMMARY === GRANTED ||
      subscriptionState?.subscription_entitlements?.some(
        ent =>
          ent.feature_name === EXPORT_PDF_SUMMARY && ent.is_enabled === true,
      )

    if (!hasPermission) {
      checkEntitlement(EXPORT_PDF_SUMMARY, i18n.t("downloadBudget.noSummary"))
      setBriefingPosition(false)
      setDownloadBudgetConfig(tipo, INPUTS.BRIEFING_POSITION, false)
    } else {
      setBriefingPosition(next)
      setDownloadBudgetConfig(tipo, INPUTS.BRIEFING_POSITION, next)
    }
    blurActiveElement()
  }

  const pdfFirstSwitches = [
    {
      i18nKey: "downloadBudget.companyAndClientInfo",
      testId: "download-budget-pdf-company-info-switch",
      state: companyAndCustomerInfo,
      setState: setCompanyAndCustomerInfo,
      key: `pdf_${INPUTS.COMPANY_AND_CLIENT_INFO}`,
      inputKey: INPUTS.COMPANY_AND_CLIENT_INFO,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfInfoBlocked,
    },
    {
      i18nKey: "downloadBudget.generalConditions",
      testId: "download-budget-pdf-general-conditions-switch",
      state: generalConditions,
      setState: setGeneralConditions,
      key: `pdf_${INPUTS.GENERAL_CONDITIONS}`,
      inputKey: INPUTS.GENERAL_CONDITIONS,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfGeneralsBlocked,
    },
    {
      i18nKey: "downloadBudget.decomposition",
      testId: "download-budget-pdf-decomposition-switch",
      state: decomposition,
      setState: setDecomposition,
      key: `pdf_${INPUTS.PART_DECOMPOSITION}`,
      inputKey: INPUTS.PART_DECOMPOSITION,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfDecompositionBlocked,
    },
    {
      i18nKey: "downloadBudget.measurements",
      testId: "download-budget-pdf-measurements-switch",
      state: pdfMeasurements,
      setState: setPdfMeasurements,
      key: `pdf_${INPUTS.PDF_MEASUREMENTS}`,
      inputKey: INPUTS.PDF_MEASUREMENTS,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfMeasurementsBlocked,
    },
  ]

  const pdfSecondSwitches = [
    {
      i18nKey: "downloadBudget.addCover",
      testId: "download-budget-pdf-add-cover-switch",
      state: addCover,
      setState: setAddCover,
      key: `pdf_${INPUTS.ADD_COVER}`,
      inputKey: INPUTS.ADD_COVER,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfCoverBlocked,
    },
    {
      i18nKey: "downloadBudget.addSignature",
      testId: "download-budget-pdf-add-signature-switch",
      state: addSignature,
      setState: setAddSignature,
      key: `pdf_${INPUTS.ADD_SIGNATURE}`,
      inputKey: INPUTS.ADD_SIGNATURE,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfSignatureBlocked,
    },
    {
      i18nKey: "downloadBudget.addDate",
      testId: "download-budget-pdf-add-date-switch",
      state: addDate,
      setState: setAddDate,
      key: `pdf_${INPUTS.ADD_DATE}`,
      inputKey: INPUTS.ADD_DATE,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfDateBlocked,
    },
    {
      i18nKey: "downloadBudget.bordes",
      testId: "download-budget-pdf-borders-switch",
      state: bordes,
      setState: setBordes,
      key: `pdf_${INPUTS.BORDES}`,
      inputKey: INPUTS.BORDES,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
    },
  ]

  return (
    <>
      {isCertTab && (
        <OptionSwitch
          key={certificationAtOriginSwitch.key}
          {...certificationAtOriginSwitch}
        />
      )}
      {isCertTab && certificationAtOrigin ? (
        <>
          <OptionSwitch
            key={`pdf_${INPUTS.INCLUDE_UNCERTIFIED_LINE_ITEMS}`}
            i18nKey="downloadBudget.includeUncertifiedLineItems"
            testId="download-budget-pdf-include-uncertified-line-items-switch"
            state={includeUncertifiedLineItems}
            setState={setIncludeUncertifiedLineItems}
            inputKey={INPUTS.INCLUDE_UNCERTIFIED_LINE_ITEMS}
            tipo={tipo}
            setDownloadBudgetConfig={setDownloadBudgetConfig}
          />
          <OptionSwitch
            key={`pdf_${INPUTS.INCLUDE_BUDGET}`}
            i18nKey="downloadBudget.includeBudget"
            testId="download-budget-pdf-include-budget-switch"
            state={includeBudget}
            setState={setIncludeBudget}
            inputKey={INPUTS.INCLUDE_BUDGET}
            tipo={tipo}
            setDownloadBudgetConfig={setDownloadBudgetConfig}
          />
          {includeBudget ? (
            <BudgetExportSelect
              value={certificationPdfFormat}
              options={certificationPdfFormatOptions}
              onChange={handleCertificationPdfFormatChange}
            />
          ) : null}
        </>
      ) : (
        <>
          {fromTab === DownloadBudgetEnum.DEFAULT && (
            <BudgetExportSelect
              inputRef={clientPriceRef}
              value={clientPrice || selectPriceOptions[0].value}
              options={selectPriceOptions}
              onChange={event => {
                const v = event.target.value
                setClientPrice(v)
                setDownloadBudgetConfig(tipo, INPUTS.PDF_PRICE_OPTION, v)
                blurActiveElement()
              }}
            />
          )}

          {fromTab !== DownloadBudgetEnum.FROM_CERTIFICATION &&
            fromTab !== DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION && (
              <BudgetExportSelect
                inputRef={briefingPositionRef}
                value={
                  briefingPosition === "top" || briefingPosition === "bottom"
                    ? briefingPosition
                    : BRIEFING_SELECT_NONE
                }
                options={briefingSelectOptions}
                onChange={handleBriefingPositionChange}
              />
            )}

          {pdfFirstSwitches.map(opt => (
            <OptionSwitch key={opt.key} {...opt} />
          ))}

          {pdfSecondSwitches.map(opt => (
            <OptionSwitch key={opt.key} {...opt} />
          ))}
        </>
      )}

      {entitlementDialog}
    </>
  )
}

PdfOptions.propTypes = {
  setDownloadBudgetConfig: PropTypes.func.isRequired,
  clientPriceRef: PropTypes.any.isRequired,
  clientPrice: PropTypes.string.isRequired,
  setClientPrice: PropTypes.func.isRequired,
  companyAndCustomerInfo: PropTypes.bool.isRequired,
  setCompanyAndCustomerInfo: PropTypes.func.isRequired,
  generalConditions: PropTypes.bool.isRequired,
  setGeneralConditions: PropTypes.func.isRequired,
  decomposition: PropTypes.bool.isRequired,
  setDecomposition: PropTypes.func.isRequired,
  pdfMeasurements: PropTypes.bool.isRequired,
  setPdfMeasurements: PropTypes.func.isRequired,
  briefingPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    .isRequired,
  setBriefingPosition: PropTypes.func.isRequired,
  briefingPositionRef: PropTypes.any.isRequired,
  addCover: PropTypes.bool.isRequired,
  setAddCover: PropTypes.func.isRequired,
  addSignature: PropTypes.bool.isRequired,
  setAddSignature: PropTypes.func.isRequired,
  addDate: PropTypes.bool.isRequired,
  setAddDate: PropTypes.func.isRequired,
  bordes: PropTypes.bool.isRequired,
  setBordes: PropTypes.func.isRequired,
  fromTab: PropTypes.oneOf(Object.values(DownloadBudgetEnum)),
  certificationAtOrigin: PropTypes.bool,
  setCertificationAtOrigin: PropTypes.func,
  includeBudget: PropTypes.bool,
  setIncludeBudget: PropTypes.func,
  certificationPdfFormat: PropTypes.string,
  setCertificationPdfFormat: PropTypes.func,
  includeUncertifiedLineItems: PropTypes.bool,
  setIncludeUncertifiedLineItems: PropTypes.func,
}
export default PdfOptions
